import { useEffect, useRef } from "react";
import Actionbar from "src/components/common/actionbar/Authactionbar";
import { Message } from "src/components/common/form/Message";
import { useMouseContent } from "src/utils/mouse/mouseContent";

import { useTQC2050_R01Store } from "./Store_TQC2050_R01";

import { AgGridReact } from "ag-grid-react";
import { Content_TQC2050_R01 } from "./Content_TQC2050_R01";
import { TQC2050_R01HeaderDto, TQC2050_R01DetailDto } from "./Dto_TQC2050_R01";
import { Info_TQC2050_R01 } from "./Info_TQC2050_R01";
import { SearchSection_TQC2050_R01 } from "./SearchSection_TQC2050_R01";
import { Table_TQC2050_R01 } from "./Table_TQC2050_R01";
import { DetailTable_TQC2050_R01 } from "./DetailTable_TQC2050_R01";
import { DetailTableSecondary_TQC2050_R01 } from "./DetailTableSecondary_TQC2050_R01";
import { Images_TQC2050_R01 } from "./Images_TQC2050_R01";

export const Container_TQC2050_R01 = () => {
	
	const { 
		TQC2050_R01List, TQC2050_R01DetailList,
		initData,
	} = useTQC2050_R01Store();

	const RESIZER = {
		code: 'TQC2050_R01',
		type: 'width',
		storageKey: 'TQC2050_R01-split-width',
		min: 5,
		max: 80,
		default: 25
	} as const;
	const gridRef = useRef<AgGridReact<TQC2050_R01HeaderDto>>(null);

	
	const containerRef = useRef<HTMLDivElement>(null);
	const infoRef = useRef<HTMLDivElement>(null);
	const imagesRef = useRef<HTMLDivElement>(null);
	const isResizing = useRef(false);

	const { 
		splitValue,
		handleMouseDown,
		handleMouseUp
	} = useMouseContent({
		resizer: RESIZER,
		isResizing,
		containerRef,
		infoRef
	});

	const detailGridRef = useRef<AgGridReact<TQC2050_R01DetailDto>>(null);
	const detailGridRef2 = useRef<AgGridReact<TQC2050_R01DetailDto>>(null);
	
	// Info와 DetailTable+Images 간 리사이저 설정
	const INFO_RESIZER = {
		code: 'TQC2050_R01_INFO',
		type: 'height',
		storageKey: 'TQC2050_R01-info-split-height',
		min: 20,
		max: 80,
		default: 40
	} as const;
	
	const isResizingInfo = useRef(false);
	const { 
		splitValue: infoSplitValue,
		handleMouseDown: handleInfoMouseDown,
		handleMouseUp: handleInfoMouseUp
	} = useMouseContent({
		resizer: INFO_RESIZER,
		isResizing: isResizingInfo,
		containerRef: infoRef,
	});
	
	// DetailTable 1과 Images 간 리사이저 설정
	const DETAIL_RESIZER = {
		code: 'TQC2050_R01_DETAIL',
		type: 'width',
		storageKey: 'TQC2050_R01-detail-split-width',
		min: 20,
		max: 60,
		default: 33
	} as const;
	
	const isResizingDetail = useRef(false);
	const detailTable1Ref = useRef<HTMLDivElement>(null);
	const {
		splitValue: detailSplitValue,
		handleMouseDown: handleDetailMouseDown,
		handleMouseUp: handleDetailMouseUp
	} = useMouseContent({
		resizer: DETAIL_RESIZER,
		isResizing: isResizingDetail,
		containerRef: infoRef,
		infoRef: detailTable1Ref
	});

	// Images와 DetailTable 2 간 리사이저 설정
	const IMAGE_DETAIL2_RESIZER = {
		code: 'TQC2050_R01_IMAGE_DETAIL2',
		type: 'width',
		storageKey: 'TQC2050_R01-image-detail2-split-width',
		min: 20,
		max: 60,
		default: 50
	} as const;
	
	const isResizingImageDetail2 = useRef(false);
	const { 
		splitValue: imageDetail2SplitValue,
		handleMouseDown: handleImageDetail2MouseDown,
		handleMouseUp: handleImageDetail2MouseUp
	} = useMouseContent({
		resizer: IMAGE_DETAIL2_RESIZER,
		isResizing: isResizingImageDetail2,
		containerRef: infoRef,
		infoRef: detailTable1Ref
	});

	const {
		fetchData,
		defaultTQC2050_R01HeaderRow,
		handleExcelClick,
		initContainerData,
		handleDeleteClick,
		handleCreateClick,
		handleSaveClick,
		handlePrintClick,
	} = Content_TQC2050_R01({
		gridRef,
		containerRef,
	});

	useEffect(() => { 
		if (TQC2050_R01List.length === 0 && TQC2050_R01DetailList.length === 0) { handleCreateClick(); }
	}, [TQC2050_R01List.length, TQC2050_R01DetailList.length, handleCreateClick]);

	return (
	<div className="content-container">
		<Actionbar
			onFetchClick={fetchData}
			onCreateClick={handleCreateClick}
			onDeleteClick={handleDeleteClick}
			onSaveClick={handleSaveClick}
			onExcelClick={handleExcelClick}
			onPrintClick={handlePrintClick}
			onInitClick={initContainerData}
		/>
		<SearchSection_TQC2050_R01 onEnterKeyDown={fetchData} />
		<div className="flex flex-1 p-[12px] gap-[4px] min-h-0" ref={containerRef}>
			<div className="h-full" style={{ width: `${splitValue}%` }}>
				<Table_TQC2050_R01 gridRef={gridRef} defaultTQC2050_R01HeaderRow={defaultTQC2050_R01HeaderRow()} />
			</div>
			{/* 리사이저 */}
			<div
				className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300"
				onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleMouseDown('LR', e.nativeEvent)}
				// 드래그 시작 방지
				onDragStart={(e: React.DragEvent<HTMLDivElement>) => e.preventDefault()}
			/>
            <div className="h-full" style={{ width: `${100 - splitValue}%` }} ref={infoRef}>
                {/* 오른쪽: 상단은 시험 정보, 하단은 가로로 디테일테이블 / 이미지 */}
                <div className="h-full flex flex-col gap-[4px]">
                    {/* 시험 정보 */}
                    <div 
                        className="w-full"
                        style={{ height: `${infoSplitValue}%` }}
                    >
                        <Info_TQC2050_R01 defaultTQC2050_R01HeaderRow={defaultTQC2050_R01HeaderRow()} />
                    </div>
                    {/* Info와 DetailTable+Images 간 수평 리사이저 */}
                    <div 
                        className="h-[6px] bg-gray-100 cursor-row-resize hover:bg-gray-300"
                        onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleInfoMouseDown("UD", e.nativeEvent)}
                        onDragStart={(e: React.DragEvent<HTMLDivElement>) => e.preventDefault()}
                    />
                    {/* 디테일1(왼쪽) / 이미지(중간) / 디테일2(오른쪽) - 가로 배치 */}
                    <div 
                        className="w-full min-h-0 flex gap-[4px] "
                        style={{ height: `${100 - infoSplitValue}%` }}
                    >
                        {/* 디테일 테이블 1 */}
                        <div 
                            className="min-w-0" 
                            ref={detailTable1Ref}
                            style={{ width: `${detailSplitValue}%` }}
                        >
                            <DetailTable_TQC2050_R01 detailGridRef={detailGridRef} />
                        </div>
                        {/* 리사이저 1 (DetailTable 1과 Images 사이) */}
                        <div 
                            className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300"
                            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleDetailMouseDown('LR', e.nativeEvent)}
                            onDragStart={(e: React.DragEvent<HTMLDivElement>) => e.preventDefault()}
                        />
                        {/* 이미지 */}
                        <div 
                            className="min-w-0 " 
                            ref={imagesRef}
                            style={{ width: `${imageDetail2SplitValue - detailSplitValue}%` }}
                        >
                            <Images_TQC2050_R01 />
                        </div>
                        {/* 리사이저 2 (Images와 DetailTable 2 사이) */}
                        <div 
                            className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300"
                            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleImageDetail2MouseDown('LR', e.nativeEvent)}
                            onDragStart={(e: React.DragEvent<HTMLDivElement>) => e.preventDefault()}
                        />
                        {/* 디테일 테이블 2 */}
                        <div 
                            className="min-w-0 h-full"
                            style={{ width: `${100 - imageDetail2SplitValue}%` }}
                        >
                            <DetailTableSecondary_TQC2050_R01 detailGridRef={detailGridRef2} />
                        </div>
                    </div>
                </div>
            </div>
		</div>
		<Message />
		
	</div>
	)
}

